import React, { useEffect, useState } from 'react';
import { AlertBehaviorType } from './behaviorTypes';

export interface AlertData {
  id: string;
  type: AlertBehaviorType;
  severity: 'warning' | 'critical';
  title: string;
  message: string;
  detectedAt: Date;
  duration?: number;
  count?: number;
  contactNotified: boolean;
}

interface AlertNotificationProps {
  alert: AlertData;
  onDismiss: (id: string) => void;
  onViewDetails?: (id: string) => void;
}

const AlertNotification: React.FC<AlertNotificationProps> = ({
  alert,
  onDismiss,
  onViewDetails
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 50);

    // Auto-dismiss after 8 seconds with progress
    const duration = 8000;
    const interval = 100;
    const decrement = (100 / (duration / interval));

    const timer = setInterval(() => {
      setProgress(prev => {
        const next = prev - decrement;
        if (next <= 0) {
          clearInterval(timer);
          handleDismiss();
          return 0;
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(alert.id), 300);
  };

  const getIcon = () => {
    switch (alert.type) {
      case 'spin':
        return (
          <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <span className="text-xl">🔄</span>
          </div>
        );
      case 'toilet':
        return (
          <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
            <span className="text-xl">🚽</span>
          </div>
        );
      case 'rest':
        return (
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <span className="text-xl">😴</span>
          </div>
        );
      case 'activity':
        return (
          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <span className="text-xl">📉</span>
          </div>
        );
      case 'scratch':
        return (
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <span className="text-xl">✋</span>
          </div>
        );
      case 'lying':
        return (
          <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center">
            <span className="text-xl">😴</span>
          </div>
        );
      case 'sitting':
        return (
          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <span className="text-xl">🪑</span>
          </div>
        );
      case 'standing':
        return (
          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <span className="text-xl">🧍</span>
          </div>
        );
      case 'walk':
        return (
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <span className="text-xl">🚶</span>
          </div>
        );
    }
  };

  const getSeverityColor = () => {
    return alert.severity === 'critical'
      ? 'border-red-500 dark:border-red-600'
      : 'border-yellow-500 dark:border-yellow-600';
  };

  const getProgressColor = () => {
    return alert.severity === 'critical' ? 'bg-red-500' : 'bg-yellow-500';
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div
      className={`
        fixed top-4 right-4 z-50 w-80 rounded-xl shadow-2xl overflow-hidden
        bg-white dark:bg-claude-darkSurface
        border-2 ${getSeverityColor()}
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      {/* Progress bar at top */}
      <div className="absolute top-0 left-0 h-1 bg-gray-200 dark:bg-gray-700 w-full">
        <div
          className={`h-full ${getProgressColor()} transition-all duration-100`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="p-4">
        <div className="flex items-start gap-3">
          {getIcon()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-gray-900 dark:text-claude-darkText truncate">
                {alert.title}
              </h4>
              <span className={`
                px-2 py-0.5 text-xs font-medium rounded-full ml-2
                ${alert.severity === 'critical'
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                }
              `}>
                {alert.severity === 'critical' ? 'CRITICAL' : 'WARNING'}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {alert.message}
            </p>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{formatTime(alert.detectedAt)}</span>
              {alert.duration && <span>Duration: {alert.duration}min</span>}
              {alert.count && <span>Count: {alert.count}/hr</span>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1">
            {alert.contactNotified && (
              <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Contact notified
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onViewDetails && (
              <button
                onClick={() => onViewDetails(alert.id)}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              >
                View Details
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Container for multiple alerts
interface AlertContainerProps {
  alerts: AlertData[];
  onDismiss: (id: string) => void;
  onViewDetails?: (id: string) => void;
}

export const AlertContainer: React.FC<AlertContainerProps> = ({
  alerts,
  onDismiss,
  onViewDetails
}) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 w-80">
      {alerts.map(alert => (
        <AlertNotification
          key={alert.id}
          alert={alert}
          onDismiss={onDismiss}
          onViewDetails={onViewDetails}
        />
      ))}
    </div>
  );
};

export default AlertNotification;
